import { useQuery } from '@tanstack/react-query'
import { Calendar } from 'lucide-react'
import {useServices} from '@/services/context'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AccessSnapshotView } from './AccessSnapshotView'
import { IdentitySnapshotView } from './IdentitySnapshotView'
import type { TimelineEvent } from '@/types/timeline'
import type { AccessSnapshot, IdentitySnapshot } from '@/types/snapshot'
import { useI18n } from '@/i18n'
import { formatDateTime } from '@/lib/date-utils'


const TAB_ACCESS = 'accessTab'
const TAB_IDENTITY = 'identityTab'


interface TimelineDetailModalProps {
    identityId: string
    event: TimelineEvent | null
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function TimelineDetailModal({
    identityId,
    event,
    open,
    onOpenChange,
}: TimelineDetailModalProps) {
    const services = useServices()
    const i18n = useI18n()
    const timestamp = event?.createdAt

    const {
        data: accessSnapshot,
        isLoading: accessLoading,
        error: accessError,
    } = useQuery({
        queryKey: [TAB_IDENTITY, identityId, 'access-snapshot', timestamp],
        queryFn: () => services.identities.accessSnapshot(identityId, {at: timestamp!}) as Promise<AccessSnapshot>,
        enabled: open && !!timestamp,
    })

    const {
        data: identitySnapshot,
        isLoading: identityLoading,
        error: identityError,
    } = useQuery({
        queryKey: [TAB_IDENTITY, identityId, 'identity-snapshot', timestamp],
        queryFn: () => services.identities.identitySnapshot(identityId, {at: timestamp!}) as Promise<IdentitySnapshot>,
        enabled: open && !!timestamp,
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        { }
                        {timestamp ? i18n.timelineDetail.stateAt({ timestamp: formatDateTime(timestamp) }) : '\u2014'}
                    </DialogTitle>
                </DialogHeader>

                <Tabs defaultValue={TAB_ACCESS} className="flex-1 overflow-hidden flex flex-col">
                    <TabsList className="w-fit">
                        <TabsTrigger value={TAB_ACCESS}>{i18n.timelineDetail.accessState}</TabsTrigger>
                        <TabsTrigger value={TAB_IDENTITY}>{i18n.timelineDetail.identityState}</TabsTrigger>
                    </TabsList>
                    <div className="flex-1 overflow-auto mt-4">
                        <TabsContent value={TAB_ACCESS} className="mt-0">
                            <AccessSnapshotView
                                data={accessSnapshot}
                                isLoading={accessLoading}
                                error={accessError}
                            />
                        </TabsContent>
                        <TabsContent value={TAB_IDENTITY} className="mt-0">
                            <IdentitySnapshotView
                                data={identitySnapshot}
                                isLoading={identityLoading}
                                error={identityError}
                            />
                        </TabsContent>
                    </div>
                </Tabs>
            </DialogContent>
        </Dialog>
    )
}
